import { createFileRoute, Link } from "@tanstack/react-router";

import { BrandLockup, HeroSubtitle } from "@/components/marketing/BrandLockup";
import FaqSection, { type FaqItem } from "@/components/marketing/FaqSection";
import HowItWorks from "@/components/marketing/HowItWorks";
import { StartFreeButton } from "@/components/marketing/MarketingButton";
import { marketingHead } from "@/components/marketing/marketingHead";
import MasteringComparison from "@/components/marketing/mastering/MasteringComparison";
import MasteringFinish from "@/components/marketing/mastering/MasteringFinish";
import MasteringFreeBand from "@/components/marketing/mastering/MasteringFreeBand";
import MasteringPrep from "@/components/marketing/mastering/MasteringPrep";
import ProductHero from "@/components/marketing/ProductHero";
import RelatedProducts from "@/components/marketing/RelatedProducts";
import Testimonials from "@/components/marketing/sections/Testimonials";
import Footer from "@/components/nav/Footer";

const LEDE =
  "Turn your final mix into a polished, release-ready master with Lutely. Preview it, hear the difference, and move straight into your release.";

export const Route = createFileRoute("/mastering")({
  component: MasteringPage,
  head: () =>
    marketingHead({
      title: "AI Music Mastering Online | Lutely",
      description: LEDE,
      path: "/mastering",
    }),
});

const FAQ: FaqItem[] = [
  {
    question: "Can mastering fix a bad mix?",
    answer:
      "Mastering works best with a balanced final mix. It adds polish, but won’t correct recording distortion or individual vocal and instrument levels.",
  },
  {
    question: "What file formats can I upload?",
    answer:
      "Lutely supports MP3, WAV, AIFF, and other audio formats. For the best result, upload your highest-quality final mix.",
  },
  {
    question: "What format is my finished master?",
    answer:
      "Your master is available to export as a 24-bit, 48 kHz WAV file. Uploading a compressed file such as an MP3 won’t restore audio detail already lost through compression.",
  },
  {
    question: "Can I compare my master with the original?",
    answer:
      "Yes. Preview both versions before deciding to export. Your original mix stays available in Audiolab.",
  },
  {
    question: "Does mastering change my production or arrangement?",
    answer:
      "No. Mastering works on the overall sound of your finished mix. Your production and arrangement remain yours.",
  },
  {
    question: "How much mastering is included?",
    answer:
      "The free plan includes up to 15 masters per month. Subscribers get unlimited mastering. Preview your masters and use them in your Lutely releases at no cost. Export a master for just $5, or use an export included with your subscription.",
  },
  {
    question: "What does the $5 export include?",
    answer:
      "The $5 export applies to the track, including its master and other available assets, such as stems. It isn’t a separate $5 charge for mastering alone.",
  },
  {
    question: "Are exports included with a subscription?",
    answer: (
      <>
        Yes. Subscription plans include monthly track exports.{" "}
        <Link to="/upgrade">See current plan allowances.</Link>
      </>
    ),
  },
];

function MasteringPage() {
  return (
    <main className="flex flex-col bg-white">
      <ProductHero
        title={
          <>
            <BrandLockup>Master</BrandLockup>
            <br />
            <HeroSubtitle>Your music online.</HeroSubtitle>
          </>
        }
        lede={LEDE}
        cta="Master a Track"
        stage={<MasteringComparison />}
      />
      <HowItWorks
        title="From final mix to finished master."
        steps={[
          {
            title: "Upload your mix",
            body: "Upload an MP3, WAV, AIFF, or another supported audio format, or choose an existing track in Audiolab.",
          },
          {
            title: "Master your track",
            body: "Lutely analyzes your audio and adjusts its overall balance, clarity, and loudness.",
          },
          {
            title: "Preview before you commit",
            body: "Listen to your master and compare it with your original. Use it in your Lutely releases at no cost, or export it for use wherever your music needs to go.",
          },
        ]}
      />
      <MasteringFinish />
      <MasteringFreeBand />
      <Testimonials
        kicker="Artist perspective"
        title="Real artists. Real results."
        className="!bg-[#F7F8FB]"
        items={[
          {
            quote:
              "Lutely delivers a clean, powerful, professional master that holds up alongside professionally released music in any environment. It saves me time and money, and I'm genuinely satisfied with the result.",
            name: "Tronee Threat",
            role: "Artist",
            image: "/marketing/tronee-threat.webp",
            href: "/@TRONEETHREAT",
          },
          {
            quote:
              "Lutely has taken the once time-consuming work of making radio edits and turned it into an on-demand, same-day task. The efficiency this has given us is immeasurable.",
            name: "Perfect Time Music Group",
            role: "Record Label",
            image: "/marketing/perfect-time-music-group.webp",
            logo: true,
          },
          {
            quote:
              "We use Lutely every day to generate radio edit and clean versions for our new releases and our back catalog. Having clean versions allows us to deliver music to more retailers… and make more money for our artists and partners.",
            name: "Next Records",
            role: "Record Label",
            image: "/marketing/next-records.webp",
            logo: true,
          },
        ]}
      />
      <MasteringPrep />
      <FaqSection items={FAQ} className="pb-8 max-[680px]:pb-8" />
      <section className="flex justify-center bg-[#F7F8FB] px-[6vw] pb-[70px] pt-4">
        <StartFreeButton className="min-w-[190px]">
          Master a Track
        </StartFreeButton>
      </section>
      <RelatedProducts current="mastering" />
      <Footer />
    </main>
  );
}
